import ValueInterface from "../../../Model/Value/ValueInterface"
import EquationValue, {
  variables,
  VariableType,
} from "../../../Model/Value/EquationValue"
import NumberValue from "../../../Model/Value/NumberValue"
import BracketedValue from "../../../Model/Value/BracketedValue"
import RandomNumberGenerator from "../../RandomNumberGenerator"
import getDivisors from "../../../util/factorize"
import { ProductSign, signs } from "../../../Model/Value/ProductValue"
import ValueGeneratorInterface from "../ValueGeneratorInterface"

type BracketPosition = "left" | "right" | "none"

export default class ProductExpressionValueGenerator implements ValueGeneratorInterface {
  constructor(
    private readonly randomNumberGenerator: RandomNumberGenerator,
    private readonly leftValueGenerator: ValueGeneratorInterface,
    private readonly rightValueGenerator: ValueGeneratorInterface,
    private readonly bracketsPosition: BracketPosition,
    private readonly minLevelRequired?: number,
  ) {}

  public supports(value: number, level: number): boolean {
    if (this.minLevelRequired !== undefined) {
      return level >= this.minLevelRequired
    }

    if (value < 5) {
      return false
    }

    const divisors: number[] = getDivisors(value)

    if (this.bracketsPosition === "none") {
      // make sure there are at least 3 other divisors -> 1, value, then three more,
      // than can be spread as a * b * c
      return divisors.length > 4
    }

    return divisors.length > 2
  }

  public generate(value: number, level: number): ValueInterface {
    // get a value from one of the generators:
    // e.g. value = 12
    // e.g. 3 + 9
    // then choose one of the sides to replace with a bracketed value
    // 3 * (3 + 3)
    // a * b
    // const a: ValueInterface = this.leftValueGenerator.generate(value)
    const divisors: number[] = getDivisors(value).filter(
      (divisor: number): boolean => divisor !== value && divisor !== 1,
    )
    const a: number = this.randomNumberGenerator.pick<number>(divisors)
    const b: number = value / a

    const aValue: ValueInterface = this.leftValueGenerator.generate(a, level)
    const bValue: ValueInterface = this.rightValueGenerator.generate(b, level)

    const productSign: ProductSign =
      this.randomNumberGenerator.pick<ProductSign>(signs)
    const templates: string[] = [`a ${productSign} b`, `b ${productSign} a`]

    return new EquationValue(
      this.bracketsPosition === "left" ? new BracketedValue(aValue) : aValue,
      new NumberValue(value),
      this.bracketsPosition === "right" ? new BracketedValue(bValue) : bValue,
      this.randomNumberGenerator.pick<VariableType>(variables),
      this.randomNumberGenerator.pick<string>(templates),
    )
  }
}
